/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { Observable, of, delay, throwError, Subject, tap, map } from 'rxjs';
import {
  Collection,
  CollectionContext,
  FileMetadata,
  CommonDimensions,
  CollectionsLinks
} from 'src/app/models/collection.interface';
import { UploadProgressSubject } from 'src/app/shared/fork.join.progress';

const MOCK_COLLECTIONS: Collection[] = [
  {
    collection_id: 'c-101',
    id: 'c-101',
    name: 'HR Onboarding Documents',
    description: 'Standard documents for new hires.',
    owner_id: 'user-a',
    access_list: ['user-a', 'user-b'],
    is_private: false,
    is_hidden: false,
    context: CollectionContext.REGULATORY,
    dimensions: [
      { name: 'document_type', description: 'Policy', show_advisor_filter: true },
      { name: 'department', description: 'HR', show_advisor_filter: false }
    ]
  } as Collection,
  {
    collection_id: 'c-102',
    id: 'c-102',
    name: 'Q4 Financial Reports',
    description: 'Confidential internal reports.',
    owner_id: 'user-c',
    access_list: ['user-c'],
    is_private: true,
    is_hidden: true,
    context: CollectionContext.RFP,
    dimensions: [{ name: 'security_level', description: 'Top Secret', show_advisor_filter: false }]
  } as Collection
];

const MOCK_FILE_METADATA: FileMetadata[] = [
  {
    collection_id: 'c-101',
    file_id: 'f-201',
    processing_stage: 'complete',
    original_document_uri: '/docs/handbook',
    s3_output_path: 's3://hr/handbook.pdf',
    catalogue: { company: 'Acme Corp', state: 'CA', year: '2024', title: 'Policy Handbook' } as CommonDimensions
  },
  {
    collection_id: 'c-101',
    file_id: 'f-202',
    processing_stage: 'complete',
    original_document_uri: '/docs/contract',
    s3_output_path: 's3://hr/contract.doc',
    catalogue: { company: 'Acme Corp', state: 'CA', year: '2024', title: 'Contract Template' } as CommonDimensions
  }
];

@Injectable()
export class MockCollectionsService {
  private readonly mockConfig = { driftApi: '/mock-api' };

  public get config() {
    return this.mockConfig;
  }

  getCollectionData(collectionId: string): Observable<FileMetadata[]> {
    const files = MOCK_FILE_METADATA.filter(f => f.collection_id === collectionId);
    if (files.length > 0) {
      return of(files).pipe(delay(2000));
    }
    if (MOCK_COLLECTIONS.some(c => c.collection_id === collectionId)) {
      return of([]);
    }
    return throwError(() => new Error('Collection not found (Mock 404)')).pipe(delay(100));
  }

  updateCollection(collectionId: string, collectionData: Collection): Observable<Collection> {
    const collectionIndex = MOCK_COLLECTIONS.findIndex(c => c.collection_id === collectionId);
    if (collectionIndex !== -1) {
      const updatedCollection = { ...MOCK_COLLECTIONS[collectionIndex], ...collectionData };
      MOCK_COLLECTIONS[collectionIndex] = updatedCollection;
      return of(updatedCollection).pipe(delay(2000));
    }
    return throwError(() => new Error('Update failed (Mock 400)')).pipe(delay(100));
  }

  createNewCollection(collectionData: Collection): Observable<Collection> {
    const newId = `c-${Math.floor(Math.random() * 1000) + 200}`;
    const newCollection = {
      ...collectionData,
      collection_id: newId,
      id: newId,
      owner_id: 'mock-owner'
    };
    MOCK_COLLECTIONS.push(newCollection);
    return of(newCollection).pipe(delay(2000));
  }

  uploadFilesToCollection(
    _collectionId: string,
    files: FileList,
    progressSubject: Subject<UploadProgressSubject>
  ): Observable<object> {
    const fileArray = Array.from(files);

    const mockLinks: CollectionsLinks = {
      links: [fileArray.map(file => ({ file_name: file.name, url: `/mock-upload/${file.name}` }))[0]]
    };

    return of(mockLinks).pipe(
      delay(100),
      tap(() => {
        progressSubject.next({ index: 1, progress: 10, total: 100 });
      }),
      delay(200),
      tap(() => {
        progressSubject.next({ index: 1, progress: 50, total: 100 });
      }),
      delay(200),
      tap(() => {
        progressSubject.next({ index: 1, progress: 100, total: 100 });
      }),
      delay(2000),
      map(() => ({}))
    );
  }

  updateFileMetadata(collectionId: string, fileId: string, metadata: CommonDimensions): Observable<FileMetadata> {
    const fileIndex = MOCK_FILE_METADATA.findIndex(f => f.collection_id === collectionId && f.file_id === fileId);
    const file = MOCK_FILE_METADATA[fileIndex];

    if (file) {
      const updatedFile = { ...file, catalogue: metadata };
      MOCK_FILE_METADATA[fileIndex] = updatedFile;
      return of(updatedFile).pipe(delay(2000));
    }
    return throwError(() => new Error('File not found (Mock 404)')).pipe(delay(100));
  }

  deleteFilesFromCollection(_collectionId: string, _fileIds: string[]): Observable<void> {
    return of(undefined).pipe(delay(200));
  }

  getCollections(includeHidden = false): Observable<Collection[]> {
    const filtered = MOCK_COLLECTIONS.filter(c => includeHidden || !c.is_hidden);
    return of(filtered).pipe(delay(500));
  }

  downloadFileFromCollection(
    _collectionId: string,
    _original_document_uri: string
  ): Observable<{ file_name: string; url: string }> {
    return of({ file_name: 'downloaded-file.pdf', url: '/mock-download-url' }).pipe(delay(2000));
  }

  copyDocumentsToCollection(
    _sourceCollectionId: string,
    _targetCollectionId: string,
    _documentIds: string[]
  ): Observable<void> {
    return of(undefined).pipe(delay(400));
  }

  moveDocumentsToCollection(
    _sourceCollectionId: string,
    _targetCollectionId: string,
    _documentIds: string[]
  ): Observable<void> {
    return of(undefined).pipe(delay(400));
  }
}
